import { ArrowDownTrayIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';

import { riskSeveritySortOrder } from '@/app/constant';
import {
  Agents as AgentsIcon,
  Assets as AssetsIcon,
  RiskTag,
  Seeds as SeedsIcon,
  TagSeverity,
  Vulnerabilities as VulnerabilitiesIcon,
} from '@/components/chariot-ui';
import { Loader } from '@/components/Loader';
import { Popover } from '@/components/Popover';
import { Button } from '@/components/ui/button';
import { useDownloadFile } from '@/hooks';
import { useGetDownloads } from '@/hooks/download';
import { JobStatus, RiskSeverity, SeverityDef } from '@/types';
import { formatDate } from '@/utils/date.util';

import { DownloadIconBadge } from './DownloadIconBadge';
import {
  ExportType,
  getEntityLabel,
  getJobType,
  getOutputPath,
} from './exportUtils';

interface Download {
  type: ExportType;
  key: string;
  processing: boolean;
  failed: boolean;
  updated: string;
  riskSeverities: RiskSeverity[];
  format: string;
}

function EntityIconCircle({ type }: { type: Download['type'] }) {
  const iconClass = 'size-4 shrink-0';
  const icon = (() => {
    switch (type) {
      case 'asset':
        return <AssetsIcon className={`${iconClass} stroke-white`} />;
      case 'seed':
        return <SeedsIcon className={`${iconClass} fill-white`} />;
      case 'risk':
        return <VulnerabilitiesIcon className={`${iconClass} fill-white`} />;
      case 'technology':
        return <GlobeAltIcon className={`${iconClass} stroke-white`} />;
      case 'agent':
        return <AgentsIcon className={`${iconClass} text-white`} />;
    }
  })();

  return (
    <div className="bg-chariot-brand-primary flex size-8 shrink-0 items-center justify-center rounded-full">
      {icon}
    </div>
  );
}

export function GlobalDownload() {
  const [open, setOpen] = useState(false);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const prevCompletedKeysRef = useRef<Set<string>>(new Set());
  const isFirstPollRef = useRef(true);

  const { mutateAsync: downloadFile } = useDownloadFile();

  // Always poll regardless of whether the dropdown is open
  const { data: exportingFileJobs, status: exportingFileJobsStatus } =
    useGetDownloads({ enabled: true });

  const { recentDownloads, processingCount } = useMemo(() => {
    const updatedRecentDownloads = exportingFileJobs
      .filter(job => {
        return (
          (job.status.toString().startsWith(JobStatus.Queued) ||
            job.status.toString().startsWith(JobStatus.Running) ||
            job.status.toString().startsWith(JobStatus.Pass) ||
            job.status.toString().startsWith(JobStatus.Fail)) &&
          getJobType(job) !== null
        );
      })
      .map(job => {
        const type = getJobType(job);
        if (!type) return null;

        const outputPath = getOutputPath(job);
        const isRisk = type === 'risk';

        const riskQueryFromKey = isRisk
          ? job.key.match(/(?<=status:)O\w?/g)
          : undefined;

        const exportedRiskSeverities: RiskSeverity[] = isRisk
          ? ((riskQueryFromKey
              ?.map(query => query[1])
              .filter(severity => severity) || []) as RiskSeverity[])
          : [];

        const statusStr = job.status.toString();
        const isProcessing =
          statusStr.startsWith(JobStatus.Running) ||
          statusStr.startsWith(JobStatus.Queued);
        const isFailed = statusStr.startsWith(JobStatus.Fail);

        return {
          key: outputPath,
          processing: isProcessing,
          failed: isFailed,
          updated: job.updated,
          type,
          riskSeverities: exportedRiskSeverities.sort((a, b) => {
            return (
              riskSeveritySortOrder.indexOf(a) -
              riskSeveritySortOrder.indexOf(b)
            );
          }),
          format: (() => {
            if (outputPath.endsWith('.pdf')) return 'pdf';
            if (outputPath.endsWith('.json')) return 'json';
            return 'csv';
          })(),
        } satisfies Download;
      })
      .filter((d): d is Download => d !== null)
      .sort(
        (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
      );

    const processingCount = updatedRecentDownloads.filter(
      d => d.processing
    ).length;

    return {
      recentDownloads: updatedRecentDownloads,
      processingCount,
    };
  }, [exportingFileJobs]);

  // Track newly completed exports to increment unviewed count
  useEffect(() => {
    const completedDownloads = recentDownloads.filter(
      d => !d.processing && !d.failed
    );
    const currentCompletedKeys = new Set(completedDownloads.map(d => d.key));

    // On first poll, seed the ref without incrementing count
    if (isFirstPollRef.current) {
      isFirstPollRef.current = false;
      prevCompletedKeysRef.current = currentCompletedKeys;
      return;
    }

    let newCount = 0;
    for (const key of currentCompletedKeys) {
      if (!prevCompletedKeysRef.current.has(key)) {
        newCount++;
      }
    }

    if (newCount > 0) {
      setUnviewedCount(prev => prev + newCount);
    }

    prevCompletedKeysRef.current = currentCompletedKeys;
  }, [recentDownloads]);

  // Clear unviewed count when the dropdown opens
  function handleSetOpen(value: boolean) {
    setOpen(value);
    if (value) {
      setUnviewedCount(0);
    }
  }

  const downloadsExist = recentDownloads.length > 0;

  return (
    <Popover
      open={open}
      setOpen={handleSetOpen}
      className="overflow-hidden p-0"
      placement="bottom"
      allowFlip={false}
      container={
        <div data-testid="global-download-icon">
          <DownloadIconBadge
            processingCount={processingCount}
            unviewedCount={unviewedCount}
          >
            <Button
              variant="ghost"
              size="icon"
              className="hover:bg-transparent"
              onClick={() => {}}
              aria-label="Exports"
            >
              <ArrowDownTrayIcon className="text-chariot-brand-secondary size-8 cursor-pointer select-none" />
            </Button>
          </DownloadIconBadge>
        </div>
      }
    >
      <div className="flex w-[380px] flex-col">
        {/* Sticky header */}
        <div className="border-chariot-stroke flex items-center justify-between border-b px-4 py-3">
          <p className="text-chariot-text-primary text-sm font-semibold">
            Exports
          </p>
          {downloadsExist && (
            <p className="text-chariot-text-secondary text-xs">
              {recentDownloads.length} recent
            </p>
          )}
        </div>

        {/* Scrollable content */}
        <div className="max-h-[400px] overflow-y-auto">
          <Loader
            isLoading={exportingFileJobsStatus === 'pending'}
            type="spinner"
            className="h-48"
          >
            {!downloadsExist && (
              <div className="flex flex-col items-center justify-center gap-2 py-10">
                <ArrowDownTrayIcon className="text-chariot-text-secondary size-8" />
                <p className="text-chariot-text-secondary text-sm">
                  No recent exports
                </p>
              </div>
            )}
            <div className="divide-chariot-stroke divide-y">
              {recentDownloads.map(download => (
                <div
                  key={download.key}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <EntityIconCircle type={download.type} />

                  <div className="min-w-0 flex-1">
                    <p className="text-chariot-text-primary text-sm font-medium">
                      {getEntityLabel(download.type)}
                    </p>

                    {download.processing ? (
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <Loader isLoading type="spinner" className="size-3" />
                        <p className="text-chariot-text-secondary text-xs">
                          Processing
                        </p>
                      </div>
                    ) : download.failed ? (
                      <p className="text-chariot-status-critical mt-0.5 text-xs">
                        Failed
                      </p>
                    ) : (
                      <p className="text-chariot-text-secondary mt-0.5 text-xs">
                        {formatDate(download.updated)}
                      </p>
                    )}

                    {/* Risk severity tags */}
                    {!download.processing &&
                      !download.failed &&
                      download.type === 'risk' && (
                        <>
                          {download.riskSeverities.length === 0 && (
                            <p className="text-chariot-text-secondary mt-1 text-xs italic">
                              All Open Vulnerabilities
                            </p>
                          )}
                          {download.riskSeverities.length > 0 && (
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              {download.riskSeverities.map(severity => (
                                <RiskTag
                                  size="xs"
                                  severity={
                                    SeverityDef[
                                      severity
                                    ]?.toLowerCase() as TagSeverity
                                  }
                                  key={severity}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}
                  </div>

                  {/* Action button */}
                  {!download.processing && !download.failed && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        downloadFile({
                          name: download.key,
                          downloadName: download.key.replace('home/', ''),
                        });
                      }}
                    >
                      {download.format.toUpperCase()}
                    </Button>
                  )}

                  {download.failed && (
                    <button
                      className="text-chariot-brand-primary shrink-0 text-xs underline hover:opacity-80"
                      onClick={() => {
                        // Retry is handled by re-opening the export modal;
                        // for now close the dropdown so the user can retry.
                        handleSetOpen(false);
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Loader>
        </div>
      </div>
    </Popover>
  );
}
